import React, { useEffect, useState } from "react";
import FHIR from "fhirclient";
import "bootstrap/dist/css/bootstrap.min.css";
import BoxContainer from "./BoxContainer";
import CommunicationService from "./services/CommunicationService";
import { loadOrgConfig } from "./configs/loadConfig";
import { Modal, Button } from "react-bootstrap";
import { logEvent } from "./services/logService";
import { buildLoginAuditEvent, buildErrorAuditEvent } from "./services/auditEventBuilder";
import { useDispatch, useSelector, useStore } from "react-redux";
import { setOrgConfig, setAuthToken } from "./slices/configSlice";
import { decodeJWT } from "./utils/jwtUtils";
import { loadSmartStyles } from "./styleLoader";
import { syncProvidersToServer } from "./store/thunks/syncProvidersToServer";

function App() {
  console.log("App component initialized");
  const dispatch = useDispatch();
  const store = useStore(); // ✅ Access the current Redux store instance
  const orgConfig = useSelector((state) => state.config.orgConfig);
  const [contextPatient, setContextPatient] = useState(null);
  const [contextProvider, setContextProvider] = useState(null);
  const [contextEncounter, setContextEncounter] = useState(null);
  const [error, setError] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [urlParams, setUrlParams] = useState(null);
  const [jwtData, setJwtData] = useState(null);
  const [iss, setIss] = useState(null);
  const [fhirClient, setFhirClient] = useState(null);
  const [communicationService, setCommunicationService] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const state = params.get("state");
    const issParam = params.get("iss");
    const launch = params.get("launch");
    const launchError = params.get("error");
    const errorDescription = params.get("error_description");
    let accessToken = null;

    console.log("🔗 RAW URL Params:", params.toString());
    console.log("🔍 URL Params extracted!!!:", { state, issParam, launch, launchError, errorDescription });

    function getEnvironmentFromHostname() {
      const host = window.location.hostname;
      if (host.includes("dev.endpointhealth.ca")) return "dev";
      if (host.includes("uat.endpointhealth.ca")) return "uat";
      if (host.includes("launch.endpointhealth.ca")) return "prd";
      if (host === "localhost" || host.endsWith(".local")) return "dev";
      return "dev";
    }

    if (launchError) {
      const readable = decodeURIComponent(errorDescription || launchError);
      console.log("OAuth launch error:", readable);
      setError(`Authorization failed: ${readable}`);
      setShowErrorModal(true);
      return;
    }

    setIss(issParam);
    setUrlParams({ state, iss: issParam, launch });
    console.log("✅ SMART context set:", { state, iss: issParam, launch });
    console.log(getEnvironmentFromHostname());

    if (!state || !issParam || !launch) {
      console.log("Missing SMART parameters, redirecting to authenticate...");
      FHIR.oauth2.authorize({
        clientId: "my_web_app",
        redirectUri: window.location.href,
        scope: "launch openid fhirUser patient/*.read encounter/*.read communication/*.read offline_access",
        state: "randomStateValue",
        iss: issParam,
        launch
      });
      return;
    }

    const config = loadOrgConfig(issParam);
    console.log("🛠️ Loaded org config from issuer:", config);
    dispatch(setOrgConfig(config));

    const fetchContextData = async () => {
      try {
        const client = await FHIR.oauth2.ready();
        setFhirClient(client);

        const tokenResponse = client?.state?.tokenResponse || {};
        const accessToken = tokenResponse.access_token;
        const refreshToken = tokenResponse.refresh_token;
        const idToken = tokenResponse.id_token;

        // Decode both tokens
        const decodedAccessToken = accessToken ? decodeJWT(accessToken) : null;
        const decodedIdToken = idToken ? decodeJWT(idToken) : null;

        // Store and log decoded data
        setJwtData({
          access: decodedAccessToken,
          id: decodedIdToken
        });

        console.log("🔓 Decoded Access Token:", decodedAccessToken);
        console.log("🔓 Decoded ID Token:", decodedIdToken);
        //console.log("🪪 Raw Access Token:", accessToken);
        //console.log("🪪 Raw ID Token:", idToken);
        //console.log("🔁 Refresh Token:", refreshToken);

        // Optional: validate issuer
        if (!decodedAccessToken?.iss) {
          console.warn("🚫 Missing 'iss' in access token — cannot validate origin");
        } else {
          console.log("✅ Token issuer:", decodedAccessToken.iss);
        }

        if (tokenResponse.smart_style_url) {
          console.log("🎨 Loading SMART styles from:", tokenResponse.smart_style_url);
          loadSmartStyles(tokenResponse.smart_style_url);
        }

        const practitionerId = decodedAccessToken?.fhirUser?.split("/")[1] || null;
        const encounterId = tokenResponse.encounter;

        const fetchFHIRResources = async () => {
          const practitioner = await client.request(`Practitioner/${practitionerId}`);
          const patient = await client.patient.read();
          const encounter = encounterId
            ? await client.request(`Encounter/${encounterId}`)
            : null;

          setContextProvider(practitioner);
          setContextPatient(patient);
          if (encounter) setContextEncounter(encounter);

          const auditEvent = buildLoginAuditEvent({
            practitioner,
            patient,
            store // ✅ Pass store to audit builder
          });
          console.log("AuditEvent to log:", auditEvent);
          console.log("Access Token for logging:", accessToken);
          dispatch(setAuthToken(accessToken));
          //await logEvent(auditEvent, store);
          logEvent(auditEvent, store).catch((logErr) => {
            console.warn("⚠️ Failed to log audit event:", logErr);
          });
        };

        try {
          await fetchFHIRResources();
        } catch (e) {
          if (e?.response?.status === 401 && refreshToken) {
            console.log("🔄 Access token expired, attempting refresh...");
            await client.refresh();
            console.log("Refresh successful");
            await fetchFHIRResources();
          } else {
            throw e;
          }
        }
      } catch (error) {
        const rawMessage = error?.message || error.toString();
        console.error("Error Fetching FHIR Data:", rawMessage);
        setError(`FHIR Initialization Error: ${rawMessage}`);
        setShowErrorModal(true);

        //TODO: refactor agent and entity to use the builder functions
        const agent = contextProvider
          ? {
            who: {
              reference: `Practitioner/${contextProvider.id}`,
              display: contextProvider.name?.[0]?.text || `${contextProvider.name?.[0]?.given?.join(" ")} ${contextProvider.name?.[0]?.family}` || "Unknown"
            },
            requestor: true
          }
          : {
            who: { reference: "Practitioner/unknown", display: "Unknown" },
            requestor: true
          };

        const entity = contextPatient
          ? {
            what: {
              reference: `Patient/${contextPatient.id}`,
              display:
                contextPatient.name?.[0]?.text ||
                `${contextPatient.name?.[0]?.given?.join(" ")} ${contextPatient.name?.[0]?.family}` ||
                "Unknown"
            },
            role: { code: "1", display: "Patient" }
          }
          : {
            what: { reference: "Patient/unknown", display: "Unknown" },
            role: { code: "1", display: "Patient" }
          };

        const auditEvent = buildErrorAuditEvent({
          errorType: "update",
          error: {
            message: rawMessage,
            status: error?.status || error?.response?.status || 500
          },
          agent,
          entity,
          description: "Error updating patient record",
          store // ✅ Pass store to audit builder
        });

        console.log("AuditEvent for error:", auditEvent);
        console.log("Access Token for logging:", accessToken);
        //await logEvent(auditEvent, store);
        logEvent(auditEvent, store).catch((logErr) => {
          console.warn("⚠️ Failed to log audit event:", logErr);
        });
      }
    };

    fetchContextData();

  }, []);

  useEffect(() => {
    if (!fhirClient) return;
    setCommunicationService(new CommunicationService(fhirClient));
  }, [fhirClient]);

  useEffect(() => {
    const interval = setInterval(() => {
      console.log("⏱️ Dispatching periodic provider sync");
      dispatch(syncProvidersToServer(store));
    }, 10 * 60 * 1000); //every 10 minutes

    return () => clearInterval(interval);
  }, [dispatch]);

  return (
    <div style={{ padding: "0px 5px 5px 5px", fontFamily: "Arial, sans-serif" }}>
      {fhirClient && communicationService && orgConfig ? (
        <BoxContainer
          contextProvider={contextProvider}
          contextPatient={contextPatient}
          contextEncounter={contextEncounter}
          error={error}
          fhirClient={fhirClient}
          communicationService={communicationService}
          orgConfig={orgConfig}
        />
      ) : (
        <div>Loading FHIR Client...</div>
      )}

      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Authorization Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div
            style={{
              whiteSpace: "pre-wrap",
              fontFamily: "monospace",
              fontSize: "0.95rem"
            }}
          >
            {error || "An unexpected authentication error occurred. Please try again."}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowErrorModal(false)}>
            Dismiss
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default App;